import { Box, Grid, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DataGridProps, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { compact, filter, find, flatMap, includes, map } from 'lodash';
import React, { useMemo } from 'react';

import { SlideUpdate } from 'api/slides';
import { CaseUpdate } from 'api/study';
import { useDataGridStyle } from 'components/atoms/BaseDataGrid/Style';
import { useTableEditingContext } from 'components/atoms/EditableDataGrid/TableEditingContext';
import { useRowSelectionContext } from 'components/atoms/RowSelectionContext';
import { Procedure } from 'interfaces/procedure';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { ReviewForm } from 'interfaces/reviewForm';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import { useResizeDetector } from 'react-resize-detector';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { CasesDataGrid } from './CasesDataGrid';
import { DataGridFooter, DataGridHeader } from './DataGridHeaderFooter';
import { SlidesDataGrid } from './SlidesDataGrid';
import { useEditableFields } from './useEditableFields';

const DEFAULT_ROW_HEIGHT = 41.5;
const tablePaginationBottomPadding = 6; // additional bottom padding to account for table pagination padding
const MIN_VISIBLE_ROWS_WITH_PAGINATION_FOOTER = 6;
const MIN_GRID_HEIGHT_FOR_PAGINATION_FOOTER = DEFAULT_ROW_HEIGHT * (MIN_VISIBLE_ROWS_WITH_PAGINATION_FOOTER + 3);
const disableVirtualizationForCypress = typeof window !== 'undefined' && 'Cypress' in window;
const selectedRowClassName = 'study-dashboard-row-selected';

const staticCommonDataGridProps: Partial<DataGridProps> = {
  keepNonExistentRowsSelected: true,
  disableRowSelectionOnClick: true,
  disableColumnFilter: true,
  disableColumnSelector: true,
  disableColumnMenu: true,
  disableVirtualization: disableVirtualizationForCypress,
  rowSelection: false, // handled by RowSelectionContext
  sortingMode: 'server',
  filterMode: 'server',
  getRowHeight: () => DEFAULT_ROW_HEIGHT,
};

export interface DataGridInterfaceWrapperProps {
  casesInPage: Procedure[];
  totalCases: number;
  totalSlides: number;
  isLoading: boolean;
  includedFieldKeys?: string[];
  fieldLabelOverrides?: Record<string, string>;
  excludedFieldKeys?: string[];
  disableEditing?: boolean;
  hidePagination?: boolean;
  pendingSlidesMode?: boolean;
  forceSlidesMode?: boolean;
  forceHeight?: number | string;
  onCaseDataChange?: (caseId: number, changes: Partial<CaseUpdate>, slideId?: string) => void;
  onSlideDataChange?: (slideId: string, changes: Partial<SlideUpdate>) => void;
  applyCellValueChangedClass?: (id: string | number, field: string, row: any) => boolean;
  onBulkEditSave?: (changes: { [field: string]: any }) => void;
  forms?: ReviewForm[];
  hideActionsMenu?: boolean;
  onPageChange?: (withOffset: boolean) => (event: React.ChangeEvent<unknown>, nextPage?: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  forcePage?: number;
  forcePageSize?: number;
  dataGridProps?: Partial<DataGridProps>;
  additionalSlideColumns?: GridColDef[];
  columnOrder?: readonly string[];
  hideLinksToViewer?: boolean;
  hideCaseIdColumn?: boolean;
}

export const DataGridInterfaceWrapper: React.FC<React.PropsWithChildren<DataGridInterfaceWrapperProps>> = ({
  totalCases,
  totalSlides,
  includedFieldKeys,
  fieldLabelOverrides,
  excludedFieldKeys = [],
  hidePagination,
  pendingSlidesMode,
  forceSlidesMode,
  forceHeight,
  onCaseDataChange: onCaseRowChange,
  onSlideDataChange,
  applyCellValueChangedClass,
  onBulkEditSave,
  forms,
  hideActionsMenu = false,
  onPageChange,
  onRowsPerPageChange,
  forcePage,
  forcePageSize,
  dataGridProps,
  additionalSlideColumns,
  columnOrder,
  hideLinksToViewer = false,
  hideCaseIdColumn = false,
  ...props
}) => {
  const currentStains = useMemo(
    () => flatMap(props?.casesInPage, ({ slides }) => map(slides, (slide) => slide.stainingType)),
    [props.casesInPage]
  );
  const { bulkEditMode, fieldsContext } = useTableEditingContext<Procedure, ProceduresFieldsContext>();
  const { isLoading, disableEditing } = props;

  const { queryParams, setQueryParams } = useEncodedFilters();
  const onSortModelChange = React.useCallback((sortModel: GridSortModel) => {
    setQueryParams({ sortBy: sortModel });
  }, []);
  const { isRowSelected } = useRowSelectionContext();

  const { selectedFields, orderedFields, selectedTableColumnGroup, setSelectedTableColumnGroup } = useEditableFields();
  const hasMultiplexFilterAvailable = includes(fieldsContext?.stainTypesInStudy, MULTIPLEX_STAIN_ID);
  const showMarkersColumn = !fieldsContext?.stainTypesInStudyLoading && hasMultiplexFilterAvailable;
  const effectiveSelectedFields = React.useMemo(
    () =>
      filter(
        showMarkersColumn
          ? includedFieldKeys || selectedFields
          : filter(includedFieldKeys || selectedFields, (field) => field !== 'channelMarkerTypes'),
        (field) => !includes(excludedFieldKeys, field)
      ),
    [excludedFieldKeys, includedFieldKeys, selectedFields, showMarkersColumn]
  );
  const effectiveOrderedFields = React.useMemo(() => {
    const availableOrderedFields = showMarkersColumn
      ? orderedFields
      : filter(orderedFields, (field) => field.dataKey !== 'channelMarkerTypes');
    const fieldsInRequestedOrder = includedFieldKeys
      ? compact(map(includedFieldKeys, (fieldKey) => find(availableOrderedFields, { dataKey: fieldKey })))
      : availableOrderedFields;

    return map(
      filter(fieldsInRequestedOrder, (field) => !includes(excludedFieldKeys, field.dataKey)),
      (field) =>
        fieldLabelOverrides?.[field.dataKey] ? { ...field, label: fieldLabelOverrides[field.dataKey] } : field
    );
  }, [excludedFieldKeys, fieldLabelOverrides, includedFieldKeys, orderedFields, showMarkersColumn]);

  const gridContainerSize = useResizeDetector();
  const gridHeight = gridContainerSize.height
    ? Math.max(gridContainerSize.height - tablePaginationBottomPadding, DEFAULT_ROW_HEIGHT)
    : undefined;
  const hidePaginationFooter = Boolean(gridHeight && gridHeight < MIN_GRID_HEIGHT_FOR_PAGINATION_FOOTER);

  const sx = useDataGridStyle();
  const theme = useTheme();
  const selectionAccentColor = bulkEditMode ? theme.palette.warning.main : theme.palette.primary.main;
  const selectionBackgroundColor = bulkEditMode
    ? alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.22 : 0.34)
    : alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.2 : 0.32);
  const selectionOutline = bulkEditMode
    ? `inset 0 0 0 1px ${alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.34 : 0.48)}`
    : `inset 0 0 0 1px ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.42 : 0.62)}`;

  const selectedRowSx = React.useMemo(
    () => ({
      [`& .${selectedRowClassName}, & .${selectedRowClassName}:hover`]: {
        boxShadow: selectionOutline,
      },
      [`& .${selectedRowClassName} > .MuiDataGrid-cell, & .${selectedRowClassName}:hover > .MuiDataGrid-cell`]: {
        backgroundColor: selectionBackgroundColor,
      },
      [`& .${selectedRowClassName} > .MuiDataGrid-cell:first-of-type, & .${selectedRowClassName}:hover > .MuiDataGrid-cell:first-of-type`]:
        {
          boxShadow: `inset 6px 0 0 ${selectionAccentColor}`,
        },
      [`& .${selectedRowClassName} .MuiCheckbox-root, & .${selectedRowClassName}:hover .MuiCheckbox-root`]: {
        color: selectionAccentColor,
      },
      [`& .${selectedRowClassName} > .MuiDataGrid-cell:not(:first-of-type) .MuiTypography-root, & .${selectedRowClassName}:hover > .MuiDataGrid-cell:not(:first-of-type) .MuiTypography-root`]:
        {
          fontWeight: 600,
        },
    }),
    [selectionAccentColor, selectionBackgroundColor, selectionOutline]
  );

  const mergedDataGridSx = React.useMemo(
    () => [
      sx,
      ...(Array.isArray(dataGridProps?.sx) ? dataGridProps.sx : dataGridProps?.sx ? [dataGridProps.sx] : []),
      selectedRowSx,
    ],
    [sx, dataGridProps?.sx, selectedRowSx]
  );

  const commonDataGridProps: Partial<DataGridProps> = React.useMemo(
    () => ({
      ...staticCommonDataGridProps,
      checkboxSelection: true,
      sx,
      getRowClassName: ({ id }) => (isRowSelected(id) ? selectedRowClassName : ''),
      columnHeaderHeight: bulkEditMode ? 2.5 * DEFAULT_ROW_HEIGHT : DEFAULT_ROW_HEIGHT,
      loading: isLoading,
      sortModel: queryParams.sortBy,
      onSortModelChange,
      hideFooter: Boolean(hidePagination || hidePaginationFooter),
      slots: {
        toolbar: DataGridHeader,
        footer: DataGridFooter,
      },
      slotProps: {
        toolbar: {
          totalSlides,
          totalCases,
          currentStains,
          hidePagination,
          bulkEditMode,
          isLoading,
          disableEditing,
          selectedTableColumnGroup,
          setSelectedTableColumnGroup,
          onBulkEditSave,
          forms,
          hideActionsMenu,
          onPageChange,
          onRowsPerPageChange,
          forcePage,
          forcePageSize,
          containerWidth: gridContainerSize.width,
        },
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton',
        },
        footer: {
          totalSlides,
          totalCases,
          isLoading,
          onPageChange,
          onRowsPerPageChange,
          forcePage,
          forcePageSize,
        } as {}, // small hack to avoid TS error
      },
    }),
    [
      sx,
      totalSlides,
      totalCases,
      currentStains,
      hidePagination,
      bulkEditMode,
      isLoading,
      queryParams.sortBy,
      disableEditing,
      selectedTableColumnGroup,
      setSelectedTableColumnGroup,
      onBulkEditSave,
      forms,
      hideActionsMenu,
      onPageChange,
      onRowsPerPageChange,
      forcePage,
      forcePageSize,
      hidePaginationFooter,
      gridContainerSize.width,
    ]
  );

  return (
    <Grid
      ref={gridContainerSize.ref}
      item
      sx={{
        flex: 1,
        height: forceHeight ? forceHeight : '100%',
        width: '100%',
        p: 0,
        '&.MuiGrid-item': {
          pt: 0,
        },
      }}
    >
      {gridHeight && (
        <Box data-testid="data-grid-height-wrapper" width={gridContainerSize.width} height={gridHeight}>
          {forceSlidesMode ?? queryParams?.slidesMode ? (
            <SlidesDataGrid
              {...props}
              selectedFields={effectiveSelectedFields}
              orderedFields={effectiveOrderedFields}
              fieldLabelOverrides={fieldLabelOverrides}
              additionalSlideColumns={additionalSlideColumns}
              columnOrder={columnOrder}
              hideCaseIdColumn={hideCaseIdColumn}
              dataGridProps={{ ...commonDataGridProps, ...dataGridProps, sx: mergedDataGridSx }}
              totalRows={totalSlides}
              pendingSlidesMode={pendingSlidesMode}
              onCaseRowChange={onCaseRowChange}
              onSlideDataChange={onSlideDataChange}
              applyCellValueChangedClass={applyCellValueChangedClass}
              hideLinksToViewer={hideLinksToViewer}
            />
          ) : (
            <CasesDataGrid
              {...props}
              selectedFields={effectiveSelectedFields}
              orderedFields={effectiveOrderedFields}
              hideCaseIdColumn={hideCaseIdColumn}
              dataGridProps={{ ...commonDataGridProps, ...dataGridProps, sx: mergedDataGridSx }}
              totalRows={totalCases}
              pendingSlidesMode={pendingSlidesMode}
              onCaseRowChange={onCaseRowChange}
              hideLinksToViewer={hideLinksToViewer}
            />
          )}
        </Box>
      )}
    </Grid>
  );
};
